import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import styles from './index.module.scss'
import { Form, Field } from 'react-final-form'
import cn from 'classnames'
import { useSelector } from 'react-redux'
import { maxStrLength } from '../../utilities/commonValidators'
import SearchSuggestInput from '../search-suggest-input'
import { individualMatches } from '../search-suggest-input/individualMatches'
import clientsAll from '../../graphql/queries/clientsAll'
import QUERY_KEYS from '../../graphql/queryKeys'
import { captureErrorAndNotify } from '../../utilities/errorHandlers'
import { TERMS_AND_CONDITION_STATUS_OPTIONS, TICKET_STATUS_OPTIONS, clientSearchFormValues } from './utils'

const SUGGESTION_LIMIT = 5

function toSuggestion (client, value, label = value) {
  if (value === null || value === undefined || String(value).trim() === '') return null

  return {
    id: String(client.id),
    label: String(label || value),
    value: String(value).slice(0, 100)
  }
}

function includesTerm (value, term) {
  return String(value ?? '').toLowerCase().includes(term.trim().toLowerCase())
}

function contactName (client) {
  return [client.firstName, client.lastName].filter(Boolean).join(' ')
}

function matchingSuggestions (clients, term, matches, toItem) {
  return Promise.resolve(
    clients
      .filter((client) => matches(client, term))
      .slice(0, SUGGESTION_LIMIT)
      .map(toItem)
      .filter(Boolean)
  )
}

export default function ClientsTableSearchForm ({
  onSubmit,
  onClear,
  disabled = false,
  initialValues = {},
  hideButtons = {},
  showTicketStatusSelect = false,
  hideTermsAndConditionStatus = false,
  handleExportClients
}) {
  const user = useSelector(({ user }) => user.user)
  const dataLoaders = useSelector(({ dataLoaders }) => dataLoaders)
  const [suggestionClients, setSuggestionClients] = useState([])

  useEffect(() => {
    if (!user?.haulerId || !dataLoaders?.dispatcher) return undefined

    let cancelled = false
    clientsAll({
      dataLoaders,
      queryKey: [QUERY_KEYS.clientsAll, user.haulerId, true]
    })
      .then((response) => {
        if (!cancelled) setSuggestionClients(response?.clientsAll || [])
      })
      .catch((error) => {
        if (cancelled) return
        setSuggestionClients([])
        captureErrorAndNotify(error, 'Error fetching Accounts')
      })

    return () => {
      cancelled = true
    }
  }, [dataLoaders, user?.haulerId])

  function handleClear (form) {
    form.reset({})
    onClear()
  }

  function valuesWithoutHiddenFilters (values) {
    if (!hideTermsAndConditionStatus) return values
    return { ...values, termsAndConditionStatus: undefined }
  }

  function loadUniversalSuggestions (term) {
    const matches = []
    const seen = new Set()

    suggestionClients.forEach((client) => {
      const phone = client.prettyPrimaryPhone || client.primaryPhone
      const values = individualMatches(term, [
        client.customId,
        client.name,
        contactName(client),
        phone
      ])

      values.forEach((label) => {
        if (matches.length >= SUGGESTION_LIMIT || seen.has(label.toLowerCase())) return
        const item = toSuggestion(client, label, label)
        if (!item) return
        seen.add(label.toLowerCase())
        matches.push(item)
      })
    })

    return Promise.resolve(matches)
  }

  function loadAccountIdSuggestions (term) {
    return matchingSuggestions(
      suggestionClients,
      term,
      (client) => includesTerm(client.customId, term),
      (client) => toSuggestion(client, client.customId)
    )
  }

  function loadAccountNameSuggestions (term) {
    return matchingSuggestions(
      suggestionClients,
      term,
      (client) => includesTerm(client.name, term),
      (client) => toSuggestion(client, client.name)
    )
  }

  function loadContactNameSuggestions (term) {
    return matchingSuggestions(
      suggestionClients,
      term,
      (client) => includesTerm(contactName(client), term),
      (client) => toSuggestion(client, contactName(client))
    )
  }

  function loadPhoneSuggestions (term) {
    return matchingSuggestions(
      suggestionClients,
      term,
      (client) => includesTerm(client.prettyPrimaryPhone || client.primaryPhone, term),
      (client) => toSuggestion(client, client.prettyPrimaryPhone || client.primaryPhone)
    )
  }

  const formKey = [
    initialValues.searchTerm,
    initialValues.accountId,
    initialValues.accountName,
    initialValues.contactName,
    initialValues.phoneNumber,
    initialValues.termsAndConditionStatus,
    initialValues.ticketStatusSelect
  ].join('|')

  return (
    <Form
      onSubmit={(values) => onSubmit(valuesWithoutHiddenFilters(values))}
      initialValues={initialValues}
      key={formKey}
      destroyOnUnregister>
      {({ handleSubmit, form, values }) => {
        const inputClassName = cn(styles.input, 'form-control')

        function submitField (name, nextValue) {
          form.change(name, nextValue)
          form.submit()
        }

        function renderSuggestField (name, label, loadSuggestions) {
          return (
            <label className={styles.label} key={name}>
              {label}
              <Field name={name} validate={maxStrLength(100)}>
                {({ input }) => (
                  <SearchSuggestInput
                    id={`clients_${name}`}
                    name={input.name}
                    value={input.value}
                    onChange={input.onChange}
                    onSelect={(item) => submitField(name, item.value)}
                    loadSuggestions={loadSuggestions}
                    maxLength={100}
                    className={inputClassName}
                  />
                )}
              </Field>
            </label>
          )
        }

        return (
          <form
            className={
              cn(styles.form, {
                [styles.multipleInputs]: showTicketStatusSelect
              })}
            onSubmit={handleSubmit}>
            {/*
              TODO This form-control is the last bootstrap we have in this component. Unfortunately replacing
              this styling with our own code is just out of scope for the current change.
            */}
            <label className={cn(styles.label, styles.universalLabel)}>
              Search
              <Field name='searchTerm' validate={maxStrLength(100)}>
                {({ input }) => (
                  <SearchSuggestInput
                    id='clients_universal_search'
                    name={input.name}
                    value={input.value}
                    placeholder='Search by account names, account ids, contact names, and phone numbers'
                    onChange={input.onChange}
                    onSelect={(item) => submitField('searchTerm', item.value || item.label)}
                    loadSuggestions={loadUniversalSuggestions}
                    maxLength={100}
                    className={inputClassName}
                  />
                )}
              </Field>
            </label>
            <div className={styles.inputs}>
              {renderSuggestField('accountId', 'Account ID', loadAccountIdSuggestions)}
              {renderSuggestField('accountName', 'Account Name', loadAccountNameSuggestions)}
              {renderSuggestField('contactName', 'Contact Name', loadContactNameSuggestions)}
              {renderSuggestField('phoneNumber', 'Phone Number', loadPhoneSuggestions)}
            </div>
            {(!hideTermsAndConditionStatus || showTicketStatusSelect) &&
              <div className={cn(styles.inputs, styles.secondRow)}>
                {!hideTermsAndConditionStatus &&
                  <label className={styles.label}>
                    T&C Status
                    <Field name='termsAndConditionStatus'>
                      {({ input }) => (
                        <select
                          {...input}
                          className={inputClassName}>
                          <option value=''></option>
                          {TERMS_AND_CONDITION_STATUS_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      )}
                    </Field>
                  </label>
                }
                {showTicketStatusSelect &&
                  <label className={styles.label}>
                    Filter by Ticket Status
                    <Field name='ticketStatusSelect'>
                      {({ input }) => (
                        <select
                          {...input}
                          className={inputClassName}>
                          <option value=''></option>
                          {TICKET_STATUS_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      )}
                    </Field>
                  </label>
                }
              </div>
            }
            <div className={cn(styles.btns, { [styles.btnsEnd]: hideButtons.export })}>
              {!hideButtons.export &&
                <button
                  type='button'
                  onClick={() => handleExportClients(clientSearchFormValues(valuesWithoutHiddenFilters(values)))}
                  className={cn(styles.btn, styles.exportBtn, 'dis-btn dis-btn-primary-dk')}
                  disabled={disabled}>
                  Export
                  <i className='material-icons dis-btn-icon'>get_app</i>
                </button>
              }
              <div>
                <button
                  type='submit'
                  className={cn(styles.btn, 'dis-btn dis-btn-success')}
                  disabled={disabled}>
                  Search
                  <i className='material-icons dis-btn-icon'>search</i>
                </button>
                {!hideButtons.clear &&
                  <button
                    type='button'
                    onClick={() => handleClear(form)}
                    className={cn(styles.btn, 'dis-btn dis-btn-white')}
                    disabled={disabled}>
                    Clear
                    <i className='material-icons dis-btn-icon'>clear</i>
                  </button>
                }
              </div>
            </div>
          </form>
        )
      }}
    </Form>
  )
}

ClientsTableSearchForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  handleExportClients: PropTypes.func,
  disabled: PropTypes.bool,
  initialValues: PropTypes.shape({
    searchTerm: PropTypes.string,
    accountName: PropTypes.string,
    accountId: PropTypes.string,
    contactName: PropTypes.string,
    phoneNumber: PropTypes.string,
    termsAndConditionStatus: PropTypes.string,
    ticketStatusSelect: PropTypes.string
  }),
  hideButtons: PropTypes.shape({
    clear: PropTypes.bool,
    export: PropTypes.bool
  }),
  showTicketStatusSelect: PropTypes.bool,
  hideTermsAndConditionStatus: PropTypes.bool
}
